import { useNavigate } from 'react-router';
import {
  PasswordServiceToken,
  type IPasswordService,
} from 'src/services/PasswordService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  WindowLocationServiceToken,
  type IWindowLocationService,
} from 'src/services/WindowLocationService';
import {
  AllowedLoginType,
  useApplicationServerContext,
  type LoginServerState,
} from '../../../../state';
import { setPreviousLoginType } from '../../utils';
import { type NextStepResponseType } from '../types';

const LOGIN_SMS_ROUTE = '/login/sms';

type PasswordSubmitProps = {
  onSubmitting: (isSubmitting: boolean) => void;
  onError: (error: string) => void;
  onXsrf: (xsrfToken: string) => void;
};

export function usePasswordSubmit({
  onSubmitting,
  onError,
  onXsrf,
}: PasswordSubmitProps) {
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const navigate = useNavigate();
  const postPasswordService =
    ServicesProvider.getSingleton<IPasswordService>(PasswordServiceToken);
  const windowLocationService =
    ServicesProvider.getSingleton<IWindowLocationService>(
      WindowLocationServiceToken,
      navigate,
    );

  async function submitUsernamePassword(
    username: string,
    password: string,
    xsrf: string,
    traceparent: string,
  ) {
    onSubmitting(true);

    // Submit username/password using POST /ui/login/password
    const passwordResponse = await postPasswordService.postPassword(
      username,
      password,
      xsrf,
      traceparent,
    );

    // Parse response, which contains either data or error
    // Error responses might be in two formats, see NextStepResponseType definition
    const {
      data: passwordData,
      error: passwordError,
      error_message: passwordErrorMessages,
    }: NextStepResponseType = await passwordResponse.json();
    setPreviousLoginType(AllowedLoginType.Password);
    if (
      (passwordError && passwordError.error_message) ||
      (passwordErrorMessages && passwordErrorMessages.length > 0)
    ) {
      onError(
        passwordError?.error_message?.[0] || passwordErrorMessages?.[0] || '',
      );
    } else if (passwordData) {
      // Handle only known LOGIN_SMS nextstep url
      if (passwordData.nextStep.includes(LOGIN_SMS_ROUTE)) {
        onXsrf(passwordData.xsrfToken);
        onSubmitting(false);
        navigate(LOGIN_SMS_ROUTE);
      } else {
        windowLocationService.goto(passwordData.nextStep, navigate);
      }
    }
  }

  async function submit(username: string, password: string, xsrf: string) {
    const xsrfToken: string =
      xsrf || serverState.forms.pkiSim.fields.xsrf.value || '';
    const traceparent: string = serverState.traceparent;

    return submitUsernamePassword(username, password, xsrfToken, traceparent);
  }
  return submit;
}
